/**
 * The four vendor slot cards on the New Audit Run page — per the approved
 * remodel mockup (`.uslots`): Tekion / Rapid Recon (six per-board chips) /
 * HomeNet / Website, each naming its file pattern and wearing a worded status
 * chip on the Excel-mirror pastels (Staged green, Waiting yellow, "N of 6"
 * yellow until complete). The Website card is informational: Phase 6
 * auto-chains the site scrape + comparison right after classification, so
 * there is nothing to upload — one run does everything.
 */
import React from "react"

import { AuditFileSlot, AuditFileStatus, AuditRapidReconSlot } from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { formatUploadedAt } from "../../../utils/formatAge"
import { parseUtcTimestamp } from "../../../utils/parseUtcTimestamp"

/** Short board names for the Rapid Recon chips, keyed by filename prefix. */
export const RR_SHORT_NAMES: Record<string, string> = {
  DOLANDODGE: "Dodge",
  DOLANFERNLEYCJDR: "Fernley",
  DOLANLEXUS: "Lexus",
  DOLANMAZDAKIA: "Mazda/Kia",
  DOLANTOYOTA: "Toyota",
  DOLANRECON: "Mill",
}

/**
 * "Waiting on: Tekion · HomeNet · 2 Rapid Recon boards (Fernley, Mill)" —
 * the run gate explained in words. Empty when everything is staged (or the
 * status hasn't loaded yet, when the button is dimmed anyway).
 */
export function describeWaitingOn(status: AuditFileStatus | null): string[] {
  if (!status) return []
  const parts: string[] = []
  if (!status.tekion.present) parts.push("Tekion")
  if (!status.homenet.present) parts.push("HomeNet")
  const missingBoards = status.rapid_recon.filter((slot) => !slot.present)
  if (missingBoards.length > 0) {
    const names = missingBoards.map((slot) => RR_SHORT_NAMES[slot.key] ?? slot.label).join(", ")
    parts.push(
      missingBoards.length === 1
        ? `1 Rapid Recon board (${names})`
        : `${missingBoards.length} Rapid Recon boards (${names})`
    )
  }
  return parts
}

interface Props {
  status: AuditFileStatus | null
  onDeleteFile: (filename: string) => void
}

const CELLS = THEME.problemDisplay.bucketCells

function ageOf(slot: AuditFileSlot): string {
  return formatUploadedAt(slot.modified)
}

/** When the six recon boards were staged. Shows a RANGE if they did not arrive
 *  together — six chips carrying one timestamp each would be unreadable, but a
 *  board left over from a previous day is exactly what an operator must catch,
 *  so a spread is stated rather than hidden behind the newest file. */
function describeRapidReconStaging(slots: AuditRapidReconSlot[]): string {
  const times = slots
    .filter((s) => s.present && s.modified)
    .map((s) => parseUtcTimestamp(s.modified as string).getTime())
    .filter((ms) => !Number.isNaN(ms))
  if (times.length === 0) return ""
  const oldest = Math.min(...times)
  const newest = Math.max(...times)
  const sameMinute = newest - oldest < 60_000
  if (sameMinute) return `Staged ${formatUploadedAt(new Date(newest).toISOString())}`
  return `Staged ${formatUploadedAt(new Date(oldest).toISOString())} — ${formatUploadedAt(
    new Date(newest).toISOString()
  )}`
}

/** Worded status chip on a bucket pastel — the mockup's small `.vcell`. */
function StatusChip({ text, tone }: { text: string; tone: "online" | "missing" | "pending" }) {
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 3,
        background: CELLS[tone].background,
        color: THEME.problemDisplay.bucketCellText,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  )
}

function SlotCard({ title, chip, children }: { title: string; chip: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.md,
        padding: "12px 14px",
        background: THEME.problemDisplay.legendBackground,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: THEME.colors.neutral.light.textPrimary }}>{title}</span>
        {chip}
      </div>
      {children}
    </div>
  )
}

function subStyle(): React.CSSProperties {
  return { fontSize: 11, color: THEME.colors.neutral.light.textSecondary }
}

/** A staged file row: name, age, and a delete ×. */
function FileLine({ slot, onDelete }: { slot: AuditFileSlot; onDelete: (filename: string) => void }) {
  if (!slot.present || !slot.filename) return null
  const filename = slot.filename
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11.5,
        background: THEME.colors.neutral.light.surface,
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.sm,
        padding: "4px 8px",
        minWidth: 0,
      }}
    >
      {/* Stacked, not inline: the exact timestamp is long enough that sharing
          one line truncated the filename to "VI_202…". Both matter — the name
          says which export, the time says which day. */}
      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={filename}>
          {filename}
        </span>
        <span
          style={{
            fontSize: 10.5,
            color: THEME.colors.neutral.light.textSecondary,
            whiteSpace: "nowrap",
          }}
        >
          {ageOf(slot)}
        </span>
      </div>
      <button
        type="button"
        aria-label={`Remove ${filename}`}
        onClick={() => onDelete(filename)}
        style={{
          marginLeft: "auto",
          border: "none",
          background: "none",
          color: THEME.colors.neutral.light.textSecondary,
          cursor: "pointer",
          fontSize: 13,
          lineHeight: 1,
          padding: 2,
        }}
      >
        ×
      </button>
    </div>
  )
}

export default function UploadSlotsGrid({ status, onDeleteFile }: Props) {
  const rrSlots = status?.rapid_recon ?? []
  const rrStaged = rrSlots.filter((s) => s.present).length
  const rrTotal = rrSlots.length

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
        gap: THEME.spacing.sm,
        marginBottom: THEME.spacing.md,
      }}
    >
      <SlotCard
        title="Tekion"
        chip={
          status?.tekion.present ? <StatusChip text="Staged" tone="online" /> : <StatusChip text="Waiting" tone="missing" />
        }
      >
        <span style={subStyle()}>One file · VI_*.csv — the dealer management export</span>
        {status && <FileLine slot={status.tekion} onDelete={onDeleteFile} />}
      </SlotCard>

      <SlotCard
        title="Rapid Recon"
        chip={
          rrTotal > 0 && rrStaged === rrTotal ? (
            <StatusChip text="Staged" tone="online" />
          ) : (
            <StatusChip text={`${rrStaged} of ${rrTotal || 6}`} tone="missing" />
          )
        }
      >
        <span style={subStyle()}>Six boards · *_Sales_Inventory.csv — five stores + the Mill</span>
        {describeRapidReconStaging(rrSlots) && (
          <span style={{ fontSize: 10.5, color: THEME.colors.neutral.light.textSecondary }}>
            {describeRapidReconStaging(rrSlots)}
          </span>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {rrSlots.map((slot) => (
            <span
              key={slot.key}
              title={
                slot.present
                  ? `${slot.label} — ${slot.filename} · ${ageOf(slot)}`
                  : `${slot.label} — not uploaded`
              }
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 7px",
                borderRadius: 3,
                background: slot.present ? CELLS.online.background : CELLS.missing.background,
                color: THEME.problemDisplay.bucketCellText,
              }}
            >
              {RR_SHORT_NAMES[slot.key] ?? slot.label}
            </span>
          ))}
        </div>
      </SlotCard>

      <SlotCard
        title="HomeNet"
        chip={
          status?.homenet.present ? <StatusChip text="Staged" tone="online" /> : <StatusChip text="Waiting" tone="missing" />
        }
      >
        <span style={subStyle()}>One file · InventoryReport*.xls — the website feed export</span>
        {status?.homenet.present ? (
          <FileLine slot={status.homenet} onDelete={onDeleteFile} />
        ) : (
          <span style={{ ...subStyle(), fontStyle: "italic" }}>
            Each run wants today's export — a stale file skews every website check.
          </span>
        )}
      </SlotCard>

      <SlotCard title="Website" chip={<StatusChip text="Automatic" tone="pending" />}>
        <span style={subStyle()}>
          No upload — the live site is scraped and compared right after classification.
        </span>
      </SlotCard>
    </div>
  )
}
